import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import FloatingSave from 'app/components/FloatingSave'
import FormCombo from 'app/components/form/FormCombo'
import FormPanel from 'app/components/form/FormPanel'
import FormPhone from 'app/components/form/FormPhone'
import { SmartForm, SubAction } from 'app/components/form/SmartForm'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useUser } from 'app/utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'
import type { UseLocationPhoneNumbersReturn } from './useLocationPhoneNumbers'
import { ManagedNumbersPanel } from './ManagedNumbersPanel'

export type LocationPhoneNumbersComponentProps = UseLocationPhoneNumbersReturn & {
  validator: any
}

export const LocationPhoneNumbersComponent = observer(function LocationPhoneNumbersComponent(
  props: LocationPhoneNumbersComponentProps,
) {
  const {
    location,
    trackingNumberOptions,
    selectedTrackingNumber,
    setSelectedTrackingNumber,
    smsNumberOptions,
    selectedSmsNumber,
    setSelectedSmsNumber,
    hasCallRailIntegration,
    hasTwilioNumbers,
    isDirtyForm,
    setIsDirtyForm,
    formRef,
    defaultValues,
    hiddenInputs,
    validator,
    onSubmit,
    refetchLocation,
  } = props

  const user = useUser()
  const isSuperUser = user?.type === `super`
  const [confirm] = useConfirmation()
  return (
    <>
      <SmartForm
        id="phone-numbers-settings"
        className="mt-0!"
        replace
        validator={validator}
        defaultValues={defaultValues}
        hiddenInputs={hiddenInputs}
        method="post"
        subaction={SubAction.EDIT}
        hideButtons={true}
        formRef={formRef}
        onSubmit={onSubmit}
      >
        <FormPanel title="Phone Numbers" cols={6}>
          <div className="flex flex-col gap-y-6">
            <FormPhone
              name="officeMainNumber"
              label="Office Main Number"
              cols={6}
              labelClassName="text-label-sm"
              className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm text-subtle shadow-elevation-1"
              placeholder="(555) 555-5555"
              validateLength
            />
            <FormPhone
              name="officeTransferNumber"
              label="Office Transfer Number"
              cols={6}
              labelClassName="text-label-sm"
              className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm text-subtle shadow-elevation-1"
              placeholder="(555) 555-5555"
              validateLength
            />
            {(hasCallRailIntegration || hasTwilioNumbers) && (
              <div className="flex flex-col gap-y-6">
                <input type="hidden" name="primaryTrackingNumber" value={selectedTrackingNumber?.value ?? ``} />
                <FormCombo
                  name="primaryTrackingNumber"
                  label="Primary Tracking Number"
                  cols={6}
                  labelClassName="text-label-sm"
                  placeholder="Select tracking number"
                  inputClassName="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm text-subtle shadow-elevation-1"
                  options={trackingNumberOptions}
                  value={selectedTrackingNumber}
                  useSingleFieldInput={false}
                  onChange={(option: OptionType) => {
                    setSelectedTrackingNumber(option?.id === `none` ? undefined : option)
                    setIsDirtyForm(true)
                  }}
                />
                <input type="hidden" name="primarySmsNumber" value={selectedSmsNumber?.value ?? ``} />
                <FormCombo
                  name="primarySmsNumber"
                  label="Primary SMS Number"
                  cols={6}
                  labelClassName="text-label-sm"
                  placeholder="Select SMS number"
                  inputClassName="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm text-subtle shadow-elevation-1"
                  infoTooltip={{
                    title: `Primary SMS Number`,
                    message: `The number text messages from this location are sent from. Leave it unset to send from the Primary Tracking Number.`,
                  }}
                  options={smsNumberOptions}
                  value={selectedSmsNumber}
                  useSingleFieldInput={false}
                  onChange={(option: OptionType) => {
                    setSelectedSmsNumber(option?.id === `none` ? undefined : option)
                    setIsDirtyForm(true)
                  }}
                />
              </div>
            )}
          </div>
        </FormPanel>

        <FloatingSave isDirtyForm={isDirtyForm} />
      </SmartForm>

      {isSuperUser && location?.id && (
        <div className="mt-6 grid max-w-3xl grid-cols-6 gap-x-4 gap-y-6">
          <ManagedNumbersPanel locationId={location.id} />
        </div>
      )}
    </>
  )
})
